import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useSEO, breadcrumb } from "../lib/seo";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    api.get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        if (data.category_slug) {
          api.get("/articles", { params: { category: data.category_slug } }).then(({ data: rel }) =>
            setRelated(rel.filter((r) => r.slug !== slug).slice(0, 3))
          );
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const coverAbs = article?.cover_image
    ? (article.cover_image.startsWith("http") ? article.cover_image : (typeof window !== "undefined" ? window.location.origin : "") + article.cover_image)
    : undefined;
  useSEO({
    title: article?.seo_title || article?.title,
    description: article?.seo_description || article?.excerpt,
    image: coverAbs,
    jsonLd: article && [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: coverAbs ? [coverAbs] : undefined,
        datePublished: article.created_at,
        dateModified: article.updated_at,
        author: { "@type": "Organization", name: "Pool Software Guide" },
        publisher: { "@type": "Organization", name: "Pool Software Guide", "@id": `${window.location.origin}/#org` },
        mainEntityOfPage: `${window.location.origin}/guides/${article.slug}`,
      },
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Guides", path: "/guides" },
        { name: article.title, path: `/guides/${article.slug}` },
      ]),
    ],
  });

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-500">Article not found. <Link to="/guides" className="text-sky-600 underline">Browse guides</Link></div>;
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-400">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="article-detail-page">
      <nav className="text-xs text-slate-400 mb-8">
        <Link to="/" className="hover:text-sky-600">Home</Link> / <Link to="/guides" className="hover:text-sky-600">Guides</Link>
      </nav>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900 leading-tight">{article.title}</h1>
      <p className="mt-4 text-slate-500 leading-relaxed">{article.excerpt}</p>
      {article.cover_image && <img src={article.cover_image} alt={article.title} className="w-full h-64 md:h-80 object-cover rounded-2xl mt-8" />}
      <article className="prose-pool mt-10" data-testid="article-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{article.content}</ReactMarkdown>
      </article>

      {related.length > 0 && (
        <div className="mt-16 border-t border-border pt-10">
          <h2 className="font-heading font-bold text-slate-900 text-lg mb-6">Related guides</h2>
          <div className="space-y-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/guides/${r.slug}`} className="block group">
                <h3 className="font-heading font-semibold text-slate-800 group-hover:text-sky-600 transition-colors duration-200">{r.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-1">{r.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
